import { useEffect, useRef, useState } from "react";
import { useOutletContext, Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, MONTHS } from "../api";
import type { AdminRecentRow, AdminStats, AdminUserRow, InboxMessage, Me, MonthReport } from "../types";
import { Icon } from "../components/Icon";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, PageHeader, Pager, Skeleton, usePaged } from "../components/ui";

const STATUS_TONE: Record<string, string> = {
  synced: "synced",
  pending: "pending",
  unmatched: "unmatched",
  error: "error",
  skipped: "info",
  received: "info",
};

const REPORT_TONE: Record<MonthReport["rows"][number]["status"], string> = {
  "not-started": "error",
  partial: "warn",
  ready: "pending",
  submitted: "synced",
};
const REPORT_LABEL: Record<MonthReport["rows"][number]["status"], string> = {
  "not-started": "not started",
  partial: "in progress",
  ready: "filled, not submitted",
  submitted: "submitted",
};

function MonthReportCard() {
  const now = new Date();
  const [ym, setYm] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });
  const touched = useRef(false);
  const shift = (d: number) => {
    touched.current = true;
    const x = new Date(Date.UTC(ym.year, ym.month - 1 + d, 1));
    setYm({ year: x.getUTCFullYear(), month: x.getUTCMonth() + 1 });
  };

  // First load: jump to the newest month that has a registered form.
  const formMonths = useQuery({
    queryKey: ["order-form-months"],
    queryFn: () =>
      api.get<{ latest: { year: number; month: number } | null }>(
        "/order/forms/months",
      ),
  });
  useEffect(() => {
    if (touched.current || !formMonths.isSuccess) return;
    touched.current = true;
    const l = formMonths.data?.latest;
    if (l && (l.year !== ym.year || l.month !== ym.month)) setYm(l);
  }, [formMonths.isSuccess, formMonths.data, ym.year, ym.month]);

  const q = useQuery({
    queryKey: ["month-report", ym.year, ym.month],
    queryFn: () =>
      api.get<MonthReport>(
        `/admin/month-report?year=${ym.year}&month=${ym.month}`,
      ),
    refetchInterval: 60_000,
  });
  const r = q.data;

  return (
    <Card
      title="Month completion"
      sub={
        r
          ? r.formRegistered
            ? `${MONTHS[ym.month - 1]} ${ym.year} · ${r.buyableDays} buyable days` +
              (r.dueDate ? ` · due ${r.dueDate}` : "")
            : `No order form registered for ${MONTHS[ym.month - 1]} ${ym.year}`
          : undefined
      }
      actions={
        <div className="cluster">
          <Button size="sm" variant="ghost" icon="arrow-left" onClick={() => shift(-1)}>
            <span className="hide-sm">Prev</span>
          </Button>
          <strong style={{ minWidth: 110, textAlign: "center" }}>
            {MONTHS[ym.month - 1]} {ym.year}
          </strong>
          <Button size="sm" variant="ghost" onClick={() => shift(1)}>
            <span className="hide-sm">Next</span>{" "}
            <Icon name="arrow-right" size={15} />
          </Button>
        </div>
      }
    >
      {q.isLoading ? (
        <Skeleton h={160} />
      ) : !r || r.rows.length === 0 ? (
        <p className="muted">No children set up.</p>
      ) : (
        <div className="day-list">
          {r.rows.map((row, i) => (
            <div key={i} className="day-item buying">
              <span className="day-when">
                {row.childLabel}
                <div className="small faint">
                  {row.ownerName || row.ownerEmail}
                </div>
              </span>
              <div className="day-body">
                <span className="small">
                  <b>
                    {row.daysFilled}/{r.buyableDays}
                  </b>{" "}
                  days
                  {row.opened > 0 && (
                    <span className="faint">
                      {" "}
                      · opened {row.opened}×
                    </span>
                  )}
                </span>
                <Badge tone={REPORT_TONE[row.status]}>
                  {REPORT_LABEL[row.status]}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function InboxCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ["inbox-messages"],
    queryFn: () =>
      api.get<{ messages: InboxMessage[] }>("/inbox/messages?limit=200"),
    refetchInterval: 60_000,
  });
  const replay = useMutation({
    mutationFn: (id: number) =>
      api.post<{ outcome: { status: string; detail: string } }>(
        `/inbox/messages/${id}/replay`,
      ),
    onSuccess: (r) => {
      toast[r.outcome.status === "error" ? "error" : "success"](
        `Reprocessed: ${r.outcome.status} (${r.outcome.detail})`,
      );
      qc.invalidateQueries({ queryKey: ["inbox-messages"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const rows = q.data?.messages ?? [];
  const { slice: pageRows, pager } = usePaged(rows, 8);
  return (
    <Card
      title="Forwarded emails"
      sub="Every raw inbound message is archived so a failed one can be inspected or reprocessed without a re-forward."
    >
      {rows.length === 0 ? (
        <p className="muted">Nothing archived yet.</p>
      ) : (
        <div className="day-list">
          {pageRows.map((m) => (
            <div key={m.id} className="day-item buying">
              <span className="day-when">
                <Badge tone={STATUS_TONE[m.status] ?? "info"}>{m.status}</Badge>
              </span>
              <div className="day-body" style={{ minWidth: 0 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="small" style={{ wordBreak: "break-word" }}>
                    {m.from_addr ?? "?"} · {m.subject || "(no subject)"}
                  </div>
                  {m.detail && (
                    <div className="faint small" style={{ wordBreak: "break-word" }}>
                      {m.detail}
                    </div>
                  )}
                </div>
                <span className="faint small grow" style={{ textAlign: "right" }}>
                  {ago(m.created_at)}
                  {m.attempts > 1 ? ` · ${m.attempts}×` : ""}
                </span>
                <a
                  className="btn ghost sm"
                  href={`/api/inbox/messages/${m.id}/raw`}
                  target="_blank"
                  rel="noreferrer"
                >
                  .eml
                </a>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="refresh"
                  loading={replay.isPending && replay.variables === m.id}
                  onClick={() => replay.mutate(m.id)}
                >
                  Reprocess
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Pager {...pager} label="messages" />
    </Card>
  );
}

function ago(iso: string | null): string {
  if (!iso) return "never";
  const then = new Date(iso.replace(" ", "T") + (iso.includes("Z") ? "" : "Z"));
  const s = Math.floor((Date.now() - then.getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return then.toLocaleDateString();
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export function Admin() {
  const me = useOutletContext<Me>();
  if (!me.isAdmin) return <Navigate to="/" replace />;

  const q = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () => api.get<AdminStats>("/admin/stats"),
    refetchInterval: 60_000,
  });

  if (q.isLoading) return <Skeleton h={400} />;
  if (q.error)
    return (
      <div className="toast error">
        <Icon name="alert-circle" size={18} />
        <span>{(q.error as Error).message}</span>
      </div>
    );

  const s = q.data!;

  return (
    <>
      <PageHeader title="Usage" desc="How the app is being used. Refreshes every minute." />

      <div className="stat-grid">
        <Stat
          label="People"
          value={s.users.total}
          sub={`${s.users.active7d} active this week · +${s.users.new30d} in 30d`}
        />
        <Stat label="Children set up" value={s.rules} />
        <Stat
          label="Lunch drafts"
          value={s.drafts.started}
          sub={
            `${s.drafts.daysPicked} days picked · ` +
            `${s.drafts.opened}/${s.drafts.started} opened the form` +
            (s.drafts.reopened ? ` · ${s.drafts.reopened} reopened` : "")
          }
        />
        <Stat
          label="Orders forwarded"
          value={s.submissions.total}
          sub={`${s.submissions.last7d} this week · ${s.submissions.last30d} in 30d`}
        />
        <Stat
          label="Emails processed"
          value={s.emails.total}
          sub={`${s.emails.last7d} this week`}
        />
        <Stat label="Calendar events created" value={s.events} />
        <Stat
          label="Menus loaded"
          value={s.menus.total}
          sub={
            s.menus.latest
              ? `latest: ${MONTHS[s.menus.latest.month - 1]} ${s.menus.latest.year} (${s.menus.latest.source})`
              : "none"
          }
        />
      </div>

      <MonthReportCard />

      <Card title="Orders by status">
        <div className="cluster">
          {(["synced", "pending", "unmatched", "error"] as const).map((k) => (
            <Badge key={k} tone={STATUS_TONE[k]}>
              {s.submissions.byStatus[k] ?? 0} {k}
            </Badge>
          ))}
        </div>
        <p className="small muted" style={{ marginTop: 10 }}>
          App inbox:{" "}
          {s.inbox.ok ? (
            <Badge tone="synced" dot>
              reachable
            </Badge>
          ) : (
            <Badge tone="error" dot>
              unreachable
            </Badge>
          )}{" "}
          {s.inbox.detail}
        </p>
      </Card>

      <InboxCard />

      <PeopleCard users={s.perUser} />

      <RecentOrdersCard rows={s.recent} />
    </>
  );
}

function PeopleCard({ users }: { users: AdminUserRow[] }) {
  const { slice, pager } = usePaged(users, 8);
  return (
    <Card
      title="People"
      sub={`${users.length} account${users.length === 1 ? "" : "s"}`}
    >
      <div className="day-list">
        {slice.map((u) => (
          <div key={u.id} className="person">
            <div className="cluster" style={{ minWidth: 0 }}>
              {u.picture ? (
                <img className="person-av" src={u.picture} alt="" />
              ) : (
                <span className="person-av avatar-fallback">
                  {(u.name || u.email)[0]?.toUpperCase()}
                </span>
              )}
              <div style={{ minWidth: 0 }}>
                <div className="person-name">{u.name || u.email}</div>
                {u.name && <div className="small faint">{u.email}</div>}
              </div>
            </div>
            <div className="person-meta">
              <span>
                <b>{u.children}</b> child{u.children === 1 ? "" : "ren"}
              </span>
              <span>
                <b>{u.daysPicked}</b> day{u.daysPicked === 1 ? "" : "s"} picked
              </span>
              <span
                className={
                  u.draftsStarted > 0 && u.draftsOpened < u.draftsStarted
                    ? "warn-text"
                    : undefined
                }
              >
                <b>
                  {u.draftsOpened}/{u.draftsStarted}
                </b>{" "}
                opened the form
                {u.draftsReopened > 0 ? `, ${u.draftsReopened} reopened` : ""}
              </span>
              <span>
                <b>{u.submissions}</b> order{u.submissions === 1 ? "" : "s"}
              </span>
              <span>
                <b>{u.events}</b> event{u.events === 1 ? "" : "s"}
              </span>
              <span className="faint">seen {ago(u.last_seen_at)}</span>
            </div>
          </div>
        ))}
      </div>
      <Pager {...pager} label="accounts" />
    </Card>
  );
}

function RecentOrdersCard({ rows }: { rows: AdminRecentRow[] }) {
  const { slice, pager } = usePaged(rows, 8);
  return (
    <Card title="Recent orders">
      {rows.length === 0 ? (
        <p className="muted">No orders yet.</p>
      ) : (
        <>
          <div className="day-list">
            {slice.map((r, i) => (
              <div key={i} className="day-item buying">
                <span className="day-when">{r.student}</span>
                <div className="day-body">
                  <span className="muted small">
                    {MONTHS[r.month - 1]} {r.year}
                  </span>
                  <Badge tone={STATUS_TONE[r.status] ?? "info"}>
                    {r.status}
                  </Badge>
                  <span
                    className="faint small grow"
                    style={{ textAlign: "right" }}
                  >
                    {ago(r.at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <Pager {...pager} label="orders" />
        </>
      )}
    </Card>
  );
}
